/**
 * Journal Entries List (UI-16 v3 layout, BE-16 data)
 *
 * Industry-standard pattern (QuickBooks/Xero/Zoho):
 * - Chronological list with source category / type filter (server-side, several types per category)
 * - Date range filter
 * - Row click -> detail page with Dr/Cr line items
 * - Account filter from a Balance Sheet line (?account=&to=), removable
 *
 * Route: /accounting/journal-entries
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Alert, Chip, Typography, FormControl, InputLabel,
  Select, MenuItem, ToggleButton, ToggleButtonGroup, TablePagination, } from '@mui/material';
import { Add } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, CurrencyText, JournalExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { SOURCE_GROUPS, sourceInfo } from '../../utils/accounting/journalSources';
import { journalEntryService } from '../../services/accounting/journalEntry.service';
import { serverMessage } from '../../utils/apiConvert';

const DATE_RANGES = [
  { label: 'All Time', value: 'ALL' },
  { label: 'This Month', value: 'MONTH' },
  { label: 'This Quarter', value: 'QUARTER' },
  { label: 'This Year', value: 'YEAR' },
];

const rangeStart = (range) => {
  const now = new Date();
  if (range === 'MONTH') return toYmd(new Date(now.getFullYear(), now.getMonth(), 1));
  if (range === 'QUARTER') return toYmd(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1));
  if (range === 'YEAR') return toYmd(new Date(now.getFullYear(), 0, 1));
  return undefined;
};

const COLUMNS = [
  { key: 'entryNumber', label: 'Entry #', width: '14%' },
  { key: 'entryDate', label: 'Date', width: '10%', render: (r) => formatDate(r.entryDate) },
  { key: 'description', label: 'Description', width: '32%' },
  {
    key: 'sourceType', label: 'Source', width: '12%',
    render: (r) => {
      const s = sourceInfo(r.sourceType);
      return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: '#fff', fontSize: '0.65rem', height: 22 }} />;
    },
  },
  { key: 'totalDebits', label: 'Debits', width: '12%', align: 'right', render: (r) => <CurrencyText amount={r.totalDebits} /> },
  { key: 'totalCredits', label: 'Credits', width: '12%', align: 'right', render: (r) => <CurrencyText amount={r.totalCredits} /> },
  {
    key: 'status', label: 'Status', width: '8%',
    render: (r) => (
      <Chip label={r.status} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: r.status === 'POSTED' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: r.status === 'POSTED' ? '#22c55e' : '#f59e0b' }} />
    ),
  },
];

const JournalEntries = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = searchParams.get('account') || '';
  const accountTo = searchParams.get('to') || '';
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const typeOptions = useMemo(
    () => SOURCE_GROUPS.find((g) => g.value === categoryFilter)?.types || [],
    [categoryFilter],
  );

  const handleCategoryChange = (val) => {
    setCategoryFilter(val);
    setTypeFilter('ALL');
    setPage(0);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const sourceTypes = typeFilter !== 'ALL' ? [typeFilter] : typeOptions;
    journalEntryService.getAll({
      sourceTypes, from: rangeStart(dateRange), to: accountTo || undefined, account: account || undefined, page, size: rowsPerPage,
    })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) { setError(serverMessage(err, 'Failed to load journal entries')); setData({ content: [], totalElements: 0 }); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [typeFilter, typeOptions, dateRange, account, accountTo, page, rowsPerPage]);

  const isTcAdmin = currentUser?.role === 'TC_ADMIN';

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Journal Entries</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <JournalExportMenu from={rangeStart(dateRange) || '2000-01-01'} to={accountTo || toYmd(new Date())} onError={setError}
            filtered={typeFilter !== 'ALL' || Boolean(account)} />
          {isTcAdmin && (
            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => navigate('/accounting/journal-entries/new')} sx={{ textTransform: 'none' }}>
              Manual Entry
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Account filter from a Balance Sheet line */}
      {account && (
        <Box sx={{ mb: 1 }}>
          <Chip
            label={`Account ${account}${accountTo ? ` · up to ${formatDate(accountTo)}` : ''}`}
            onDelete={() => { setSearchParams({}); setPage(0); }}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: fs.xs }}
          />
        </Box>
      )}

      {/* Filter Row 1: Category toggle buttons + Period dropdown */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={categoryFilter}
            exclusive
            onChange={(_, v) => v && handleCategoryChange(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { fontSize: fs.xs, px: 1.5, py: 0.4, textTransform: 'none', whiteSpace: 'nowrap' } }}
          >
            {SOURCE_GROUPS.map((g) => <ToggleButton key={g.value} value={g.value}>{g.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Period</InputLabel>
          <Select value={dateRange} label="Period" onChange={(e) => { setDateRange(e.target.value); setPage(0); }}>
            {DATE_RANGES.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
          {data.totalElements} {data.totalElements === 1 ? 'entry' : 'entries'}
        </Typography>
      </Box>

      {/* Filter Row 2: Sub-type toggle buttons (shown when category has sub-types) */}
      {typeOptions.length > 1 && (
        <Box sx={{ mb: 1.5, maxWidth: '100%', overflowX: 'auto' }}>
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, v) => { if (v) { setTypeFilter(v); setPage(0); } }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { fontSize: fs.xs, px: 1.5, py: 0.3, textTransform: 'none', whiteSpace: 'nowrap' } }}
          >
            <ToggleButton value="ALL">All</ToggleButton>
            {typeOptions.map((t) => (
              <ToggleButton key={t} value={t}>{sourceInfo(t).label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Table */}
      <DataTable
        columns={COLUMNS}
        data={data.content}
        loading={loading}
        emptyMessage="No journal entries found."
        onRowClick={(row) => navigate(`/accounting/journal-entries/${row.id}`)}
        pagination={false}
      />
      {data.totalElements > 0 && (
        <TablePagination
          component="div"
          count={data.totalElements}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}
    </Box>
  );
};

export default JournalEntries;
